import { EMAIL_PURPOSES } from '../../../../shared/widgets.js'
import { useMe } from '../../../lib/queries/session.js'
import { useWidgetContext, useWidgetState } from './context.js'
import { Card, CopyButton, Watch, asList } from './primitives.jsx'
import { emailDraftValue, emailMessage } from './text.js'
import w from './widgets.module.css'

// Template pills ask the model for a new draft; the client holds no templates.
const TEMPLATES = ['samples', 'credentials', 'questions', 'decisions', 'status']

/**
 * #9 Client email draft: the subject and the message, both editable in place.
 * The message starts as the model's body with a greeting and the user's name;
 * the user's edits are saved with the conversation, and the model's version
 * stays in the transcript.
 */
export function EmailDraft({ input, block }) {
  const { readOnly, onAsk } = useWidgetContext()
  const { user } = useMe()
  const [state, update] = useWidgetState(block.key, null)
  // Signed by the signed-in user; a shared view shows only what the owner saved.
  const sender = state?.sender ?? (readOnly ? '' : (user?.name ?? ''))
  const value = { ...emailDraftValue(input, state), sender }
  const message = emailMessage(value)
  const locked = readOnly || !block.complete
  const client = input.client_name?.trim()
  const warnings = asList(input.warnings)

  const edit = (patch) => update((s) => ({ ...emailDraftValue(input, s), sender, ...s, ...patch }), { debounce: 400 })

  const askFor = (purpose) => {
    if (!onAsk || purpose === input.purpose) return
    onAsk(`Draft a ${EMAIL_PURPOSES[purpose].toLowerCase()} email${client ? ` for ${client}` : ''}.`)
  }

  return (
    <Card
      label="Email to client"
      title="Email to client"
      sub={[EMAIL_PURPOSES[input.purpose] ?? 'Email', client].filter(Boolean).join(' · ')}
    >
      <div className={w.rowTight}>
        {TEMPLATES.map((purpose) => (
          <button
            key={purpose}
            type="button"
            className={w.pill}
            aria-pressed={purpose === input.purpose}
            disabled={!onAsk && purpose !== input.purpose}
            title={purpose === input.purpose ? undefined : 'Ask for this email instead'}
            onClick={() => askFor(purpose)}
          >
            {EMAIL_PURPOSES[purpose]}
          </button>
        ))}
      </div>
      <div className={w.subject}>
        <span>Subject</span>
        <input
          className={w.subjectInput}
          value={value.subject}
          aria-label="Subject"
          disabled={locked}
          onChange={(e) => edit({ subject: e.target.value })}
        />
        <CopyButton text={value.subject} />
      </div>
      {locked ? (
        <div className={w.email}>{message}</div>
      ) : (
        <textarea
          className={`${w.email} ${w.emailEdit}`}
          value={message}
          aria-label="Message"
          rows={Math.max(8, message.split('\n').length + 2)}
          onChange={(e) => edit({ message: e.target.value })}
        />
      )}
      {warnings.map((warning) => (
        <Watch key={warning} label="Before sending:">
          {warning}
        </Watch>
      ))}
      <div className={w.row}>
        <span className={`${w.muted} ${w.grow}`}>
          {state ? 'Your edits are saved with this conversation.' : 'Edit the subject or the message before you send it.'}
        </span>
        <CopyButton text={() => `Subject: ${value.subject}\n\n${message}`} label="Copy email" primary small={false} />
      </div>
    </Card>
  )
}
